import {
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
} from 'firebase/firestore';
import {
    signInWithCustomToken,
    getAuth,
    User,
    setPersistence,
    browserLocalPersistence,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import fetchFirebaseToken from '../api/fetch-firebase-token';
import { FIREBASE_CONFIG, FIRESTORE_APT_DB_NAME } from '../api/constants';
import { track } from '../tracking';
import { apiRefreshResponse } from '../api/fetchWithRefresh';
import { v4 } from 'uuid';

const app = initializeApp(FIREBASE_CONFIG);
const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence);

const FIVE_MB_IN_BYTES = 5000000;

const firestore = initializeFirestore(
    app,
    {
        ignoreUndefinedProperties: true,
        localCache: persistentLocalCache(
            /*settings*/ {
                tabManager: persistentMultipleTabManager(),
                cacheSizeBytes: FIVE_MB_IN_BYTES,
            },
        ),
    },
    FIRESTORE_APT_DB_NAME,
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.firestore = firestore;

async function getFirebaseTokenWithRetry(): Promise<string> {
    const trackingId = v4();
    try {
        const firebaseTokenResponse = await fetchFirebaseToken();
        const firebaseToken = firebaseTokenResponse.token;
        if (!firebaseToken) {
            throw new Error('Firebase token not received for setup');
        }
        return firebaseToken;
    } catch (e) {
        track('GET_FIREBASE_TOKEN_WITH_RETRY', {
            trackingId,
            status: 'firebaseToken not found on initial fetch',
        });
        await apiRefreshResponse();
        const firebaseTokenResponse = await fetchFirebaseToken();
        const firebaseToken = firebaseTokenResponse.token;
        if (!firebaseToken) {
            track('GET_FIREBASE_TOKEN_WITH_RETRY', {
                trackingId,
                status: 'firebaseToken not found on retry fetch',
            });
            throw new Error('Firebase token still not received for setup');
        }
        return firebaseToken;
    }
}

async function initFirebaseAuth(): Promise<User | undefined> {
    try {
        const firebaseToken = await getFirebaseTokenWithRetry();
        if (!firebaseToken) {
            throw new Error('Firebase token not received for setup');
        }
        const userAuth = await signInWithCustomToken(auth, firebaseToken);
        return userAuth.user;
    } catch (e) {
        track('FS_AUTH_INIT_ERROR', { on: 'initFirebaseAuth', what: (e as Error).message });
        console.error('ERROR =====> initFirebaseAuth', e);
    }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.initFirebaseAuth = initFirebaseAuth;

export { app as firebase, firestore, auth as firebaseAuth, initFirebaseAuth };
